import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { VxSwal } from '@voxie/frontend-components';
import { createGlobalState } from '@vueuse/core';
import { computed, inject, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useToasted } from '~/composables/useToasted';
import { isAxiosError } from 'axios';
import { agentsClient, groupsClient, messagesClient } from '~/services';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { MAX_SELECTED_THREADS_NUMBER } from '~/constants/messageHub';
import type { Features } from '~/provides/features.types';
import type { Agent } from '~/services/agents/agentsClient.types';
import type { Group } from '~/services/groups/groupsClient.types';
import type { Thread } from '~/types/thread.types';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';

export const useMessageHub = createGlobalState(() => {
    const toasted = useToasted();
    const FEATURES = inject('FEATURES') as Features;
    const teamId = Spark.state.currentTeam.id;
    const { onCorporate, getCurrentGroupId, currentOrgUnit } = useCurrentGroup();
    const { isMd } = useResponsiveLayout();

    const route = useRoute();

    const isScheduledRoute = computed(() => {
        return ['message-hub.scheduled-messages', 'message-hub.scheduled-message'].includes(String(route.name));
    });

    const isWizardRoute = computed(() => {
        return route.name === 'message-hub.intelligent-inbox.wizard';
    });

    const isIntelligentRoute = computed(() => {
        return [
            'message-hub.intelligent-inbox',
            'message-hub.intelligent-inbox.settings',
            'message-hub.intelligent-inbox.wizard',
        ].includes(String(route.name));
    });

    const isInboxRoute = computed(() => {
        return !isScheduledRoute.value && !isIntelligentRoute.value;
    });

    /**
     * Used to get the current route.query.inbox, if the user has contamined the
     * query parameter it will default to the appropriate inbox
     */
    const currentInbox = computed(() => {
        if (FEATURES.simple_message_hub) {
            // simple message hub only has "all"
            return 'all';
        }

        const inbox = String(route.query.inbox);

        // if not valid return the default: "me"
        if (!['me', 'unassigned', 'open', 'closed', 'all'].includes(inbox)) {
            return 'me';
        }

        return inbox;
    });

    const hasPhoneNumber = ref<boolean | null>();

    const checkPhoneNumber = async () => {
        if (currentOrgUnit.value) {
            return;
        }
        if (hasPhoneNumber.value === null || hasPhoneNumber.value === true) {
            return;
        }

        hasPhoneNumber.value = null;
        try {
            if (onCorporate()) {
                await messagesClient.getPhoneNumber(teamId);
            } else {
                await messagesClient.getGroupPhoneNumber(teamId, getCurrentGroupId());
            }

            hasPhoneNumber.value = true;
        } catch (error: unknown) {
            hasPhoneNumber.value = false;
            if (isAxiosError(error) && error.response?.status === HTTP_NOT_FOUND) {
                VxSwal.fire({
                    title: 'Missing Phone Number',
                    text: 'No phone numbers are available to send messages.',
                    icon: faTriangleExclamation,
                    iconColor: 'warning',
                });
            } else {
                console.error(error);
            }
        }
    };

    const agents = ref<Agent[]>([]);

    const getAgents = async () => {
        const response = await agentsClient.getAgents({ teamId, groupId: getCurrentGroupId() });

        agents.value = response.data;
    };

    const groups = ref<Group[]>([]);
    const loadingGroups = ref(false);

    const getGroups = async () => {
        if (!currentOrgUnit.value) {
            return;
        }

        loadingGroups.value = true;
        try {
            const response = await groupsClient.getGroups(
                {
                    teamId,
                    orgUnitId: currentOrgUnit.value?.id,
                },
                {
                    minimal: 1,
                    per_page: 1000,
                }
            );

            groups.value = response.data.data;
        } catch (e) {
            console.error(e);
            toasted.error('Failed to load groups for this OU.');
        } finally {
            loadingGroups.value = false;
        }
    };

    // Thread selection state - use Map to store complete thread objects
    const selectedThreads = ref(new Map<string | number, Thread>());

    const getThreadKey = (thread: Thread): string | number => {
        return thread.group_id ? `${thread.contact_id}-${thread.group_id}` : thread.contact_id;
    };

    const toggleThreadSelection = (thread: Thread) => {
        const key = getThreadKey(thread);
        if (selectedThreads.value.has(key)) {
            selectedThreads.value.delete(key);
            return;
        }

        if (selectedThreads.value.size >= MAX_SELECTED_THREADS_NUMBER) {
            toasted.error(`You can select a maximum of ${MAX_SELECTED_THREADS_NUMBER} threads at once.`);
            return;
        }
        selectedThreads.value.set(key, thread);
    };

    const clearSelection = () => {
        selectedThreads.value.clear();
    };

    const removeThreadSelection = (thread: Thread) => {
        selectedThreads.value.delete(getThreadKey(thread));
    };

    const isThreadSelected = (thread: Thread) => {
        return selectedThreads.value.has(getThreadKey(thread));
    };

    const selectedThreadsByGroup = computed(() => {
        const grouped = new Map<string, Thread[]>();

        for (const thread of selectedThreads.value.values()) {
            const groupId = thread.group_id;

            if (!groupId) {
                continue;
            }

            if (!grouped.has(groupId)) {
                grouped.set(groupId, []);
            }
            grouped.get(groupId)!.push(thread);
        }

        return grouped;
    });

    // Watch route changes to clear selection when leaving bulk-actions
    watch(
        () => route.name,
        () => {
            if (route.name !== 'message-hub.bulk-actions' && (!currentOrgUnit.value || isMd.value)) {
                clearSelection();
            }
        }
    );

    const mountMessageHub = () => {
        if (!FEATURES.simple_message_hub) {
            getAgents();
        }

        if (currentOrgUnit.value) {
            getGroups();
        }
    };

    return {
        mountMessageHub,
        currentInbox,

        isScheduledRoute,
        isIntelligentRoute,
        isWizardRoute,
        isInboxRoute,

        checkPhoneNumber,
        hasPhoneNumber,

        agents,

        groups,
        loadingGroups,
        // Thread selection
        selectedThreads,
        getThreadKey,
        toggleThreadSelection,
        isThreadSelected,
        clearSelection,
        removeThreadSelection,
        selectedThreadsByGroup,
        MAX_SELECTED_THREADS_NUMBER,
    };
});
